import { assert } from '@sparkdotfi/common-universal'

import { NativeAssetInfo } from '@/config/chain/types'
import { TokenWithBalance } from '@/domain/common/types'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { MarketInfo, Reserve } from '@/features/sparklend/common/data/market-info/marketInfo'
import { MarketWalletInfo } from '@/features/sparklend/common/data/useMarketWalletInfo'
import { applyTransformers } from '@/utils/applyTransformers'

export interface GetBorrowOptionsParams {
  reserve: Reserve
  marketInfo: MarketInfo
  walletInfo: MarketWalletInfo
  nativeAssetInfo: NativeAssetInfo
  isInSandbox: boolean
}

export function getBorrowOptions({
  reserve,
  marketInfo,
  walletInfo,
  nativeAssetInfo,
  isInSandbox,
}: GetBorrowOptionsParams): TokenWithBalance[] {
  const options = applyTransformers({ reserve, marketInfo, walletInfo, nativeAssetInfo, isInSandbox })([
    getNativeAssetBorrowOptions,
    getUsdsBorrowOption,
    getDefaultBorrowOptions,
  ])
  assert(options, `No deposit options found for token ${reserve.token.symbol}`)

  return options
}

function getNativeAssetBorrowOptions({
  reserve,
  marketInfo,
  walletInfo,
  nativeAssetInfo,
}: GetBorrowOptionsParams): TokenWithBalance[] | undefined {
  const { nativeAssetSymbol, wrappedNativeAssetSymbol } = nativeAssetInfo

  if (reserve.token.symbol !== nativeAssetSymbol && reserve.token.symbol !== wrappedNativeAssetSymbol) {
    return undefined
  }
  const native = marketInfo.findOneReserveBySymbol(nativeAssetSymbol)
  const wrapped = marketInfo.findOneReserveBySymbol(wrappedNativeAssetSymbol)

  return [
    { token: native.token, balance: walletInfo.findWalletBalanceForToken(native.token) },
    { token: wrapped.token, balance: walletInfo.findWalletBalanceForToken(wrapped.token) },
  ]
}

function getUsdsBorrowOption({
  reserve,
  walletInfo,
  marketInfo,
  isInSandbox,
}: GetBorrowOptionsParams): TokenWithBalance[] | undefined {
  if (
    reserve.token.symbol !== TokenSymbol('USDS') ||
    (marketInfo.chainId !== 1 && !isInSandbox) ||
    import.meta.env.VITE_FEATURE_BORROW_USDC_FROM_USDS !== '1'
  ) {
    return undefined
  }

  const usdcReserve = marketInfo.findOneReserveBySymbol(TokenSymbol('USDC'))
  return [
    { token: reserve.token, balance: walletInfo.findWalletBalanceForToken(reserve.token) },
    { token: usdcReserve.token, balance: walletInfo.findWalletBalanceForToken(usdcReserve.token) },
  ]
}

function getDefaultBorrowOptions({ reserve, walletInfo }: GetBorrowOptionsParams): TokenWithBalance[] {
  return [{ token: reserve.token, balance: walletInfo.findWalletBalanceForToken(reserve.token) }]
}
